/**
 * AMD-43 — the player hull, generated per run.
 *
 * Spine §12 pins the player as a *warm-gold manta core — swept wings, notched trailing
 * edge, no tail fin*, and that silhouette is load-bearing: §11.12's category separation
 * is the promise that the one shape you must never lose track of can never be mistaken
 * for something that is trying to kill you. "A new ship every run" pulls directly against
 * "instantly recognisable ship".
 *
 * This file resolves that by varying WITHIN the family. Every parameter below moves a
 * feature the §12 sentence already names — wing sweep, wing span, notch depth, body
 * taper, the bow of the leading and trailing edges, the wingtip's chord, and a small
 * left/right asymmetry.
 * The hull is ONE closed outline, so there is no way to express a second mass, a tail fin
 * or a tentacle crown: the family bound is structural, not a matter of taste in ranges.
 *
 * Measured over 20,000 seeds (`tests/ship-hull.test.ts` asserts the same over 2,000):
 *
 *  - 20,000 distinct outlines. No collisions, and adjacent seeds are never near-duplicates.
 *  - Area overlap with the §12 reference manta at the §7.7 48 px bar: min 0.65, median 0.85.
 *  - Area overlap with the nearest of the 14 archetypes and 4 bosses: max 0.67, median 0.55
 *    — against 0.59 for the ratified manta itself, and 0.68 for the two closest shapes the
 *    roster already ships (brood-bell / abyssal-warden).
 *  - Radial-signature distance to the nearest roster shape: min 0.25, against the 0.076
 *    the roster's own closest pair (bloom-jelly / crown-medusa) sits at. Every hull is
 *    3.3× further from its nearest enemy than two shipped enemies are from each other.
 *  - Every one of the 20,000 is closer to the §12 manta than to any enemy or boss, on both
 *    measures. Snowflakes, not an arbitrary shape generator.
 *
 * Seeding is the run seed and nothing else. §7's determinism and §13.1.10's share-string
 * promise both survive: paste someone's seed and you get their run *and* their ship. No
 * gameplay stream is read — the hull seed is derived straight from `runSeed` through the
 * same `^ fnv1a32(name)` construction Appendix A.1 uses for the stream bank, so it cannot
 * consume or perturb `vfx` either.
 *
 * Everything here runs once per run, in `Renderer.install`. Nothing allocates per frame,
 * and the hull stays ONE mesh in ONE batch — §14.2 budgets the player at 2 meshes and a
 * procedural silhouette must not become N.
 */

import * as THREE from 'three';
import { RngStream, fnv1a32 } from '../core/rng';
import { ShapeBuilder } from './silhouettes';

/**
 * `hull_seed = (run_seed ^ fnv1a32('vfx.hull')) >>> 0`, the Appendix A.1 stream
 * derivation applied to a presentation-only name. Deliberately NOT a member of
 * `STREAM_NAMES`: it must be impossible for a hull to move a gameplay stream's state,
 * and impossible for a mid-run `vfx` draw to change which ship you are flying.
 */
export function hullSeed(runSeed: number): number {
  return (runSeed ^ fnv1a32('vfx.hull')) >>> 0;
}

/**
 * The hull's shape space. Every field is a §12 feature; the reference manta's own value
 * is quoted against each so a range can be read as "how far from the ratified shape".
 */
export interface HullParams {
  /** Nose reach along the heading axis. Reference 1.0. */
  noseX: number;
  /** Half-width of a blunted nose. 0 is a point; the reference is a point. */
  noseHalf: number;
  /** Shoulder — where the leading edge breaks on its way out to the wing. Reference -0.05. */
  cheekX: number;
  /** Shoulder half-width, i.e. how full the body is. Reference 0.34. */
  cheekY: number;
  /** Leading-edge bow, as a relative radius on the mid-leading-edge point. Reference 0. */
  camber: number;
  /** Wingtip x. More negative is more swept. Reference -0.5. */
  sweep: number;
  /** Wingtip y — half the wingspan. Reference 0.9. */
  span: number;
  /** How far behind the wingtip the trailing corner sits. Reference 0.28. */
  trailBack: number;
  /** Trailing corner's y as a fraction of `span`. Reference 0.556. */
  trailFrac: number;
  /** Notch depth: how far forward the trailing edge is cut. Reference 0.16. */
  notch: number;
  /** Wingtip chord: 0 is a pointed tip, 0.15 a squared-off one. Reference 0. */
  tipRake: number;
  /** Trailing-edge bow, as a relative radius on its midpoint. + fills, - swallowtails. */
  rearCamber: number;
  /** Lower wing's span as a multiple of the upper's. Reference 1. */
  asymSpan: number;
  /** Lower wingtip's extra sweep, in world units. Reference 0. */
  asymSweep: number;
  /** Lower shoulder's half-width as a multiple of the upper's. Reference 1. */
  asymCheek: number;
}

/**
 * Spine §12's ratified manta, as parameters. `buildHull(REFERENCE_HULL)` reproduces the
 * original eight-point fan exactly, which is what makes it usable as the family anchor in
 * `hullSimilarity` — the shape every generated hull is measured against for "still a
 * manta", rather than a vague appeal to one.
 */
export const REFERENCE_HULL: HullParams = {
  noseX: 1.0,
  noseHalf: 0,
  cheekX: -0.05,
  cheekY: 0.34,
  camber: 0,
  sweep: -0.5,
  span: 0.9,
  trailBack: 0.28,
  trailFrac: 0.5 / 0.9,
  notch: 0.16,
  tipRake: 0,
  rearCamber: 0,
  asymSpan: 1,
  asymSweep: 0,
  asymCheek: 1,
};

/**
 * The ranges, and why each one stops where it does. Every bound below was set by measuring
 * the population against the roster, not by taste; the tightening pass is in the report.
 *
 *  - `noseX` 0.90–1.04 and `sweep` -0.60..-0.46 are the two that had to be pulled in. A
 *    long nose on a weakly swept wing is a spearnose: at -0.42 sweep the worst hulls sat
 *    *closer to the spearnose than to their own reference manta*, which is the one thing
 *    §11.12 forbids outright. Sweep is the strongest single driver of that margin
 *    (r = -0.60); nose length is second.
 *  - `noseHalf` is bimodal — 45% of hulls have a pointed nose, the rest a blunt chord of
 *    0.03–0.055 (1.4–2.6 px at 48 px). A continuous range spends most of its mass on
 *    chords too short to resolve while still costing a vertex.
 *  - `cheekX`/`cheekY`: the shoulder. Forward and narrow gives a dart-bodied ray, back and
 *    wide a broad disc; both are mantas. Wider than 0.37 starts to read as the
 *    ventspitter's cone.
 *  - `camber` -0.12..+0.18 bows the leading edge out or scythes it back, and is the
 *    strongest *character* lever that does not move the enemy margin at all (r = 0.02).
 *  - `span` 0.84–0.96 with the aspect guard in `hullMetrics`: the pair can never meet at a
 *    needle or a disc. The top of the range came down from 1.00 with `trailBack`'s floor
 *    going up to 0.23 for one reason, measured: a wide thin wing with a pointed tip and a
 *    short trailing edge is a spearnose's fin, and those hulls owned the whole bottom of
 *    the §11.12 margin distribution.
 *  - `trailBack` 0.23–0.34 is an OFFSET from the wingtip rather than an absolute x, so a
 *    barely-swept wing can never have its trailing corner in front of its own tip.
 *  - `notch` is a FRACTION of the body behind the wingtip, floored at {@link MIN_NOTCH} so
 *    §12's notched trailing edge is always at least 4.8 px at 48 px.
 *  - `tipRake` is bimodal for the same reason `noseHalf` is: half the hulls have a pointed
 *    wingtip, half a squared blade of 0.06–0.15.
 *  - `rearCamber` -0.08..+0.13 bows the trailing edge: a straight cut, a filled sweep or a
 *    swallowtail. Added after looking at a contact sheet of 64 seeds that were all
 *    recognisably the same individual — it is the cheapest large gain in seed-to-seed
 *    difference available, and it moves the §11.12 margin by nothing measurable.
 *  - The three `asym*` are deliberately small. A wing 4% longer than its opposite reads as
 *    an individual animal; 10% reads as damage, and damage is a state the HUD owns.
 */
const R = {
  noseX: [0.9, 1.04],
  noseHalf: [0.03, 0.055],
  cheekX: [-0.16, 0.02],
  cheekY: [0.26, 0.37],
  camber: [-0.12, 0.18],
  sweep: [-0.6, -0.46],
  span: [0.84, 0.96],
  trailBack: [0.23, 0.34],
  trailFrac: [0.44, 0.64],
  notchFrac: [0.24, 0.42],
  tipRake: [0.06, 0.15],
  rearCamber: [-0.08, 0.13],
  asymSpan: [0.955, 1.045],
  asymSweep: [-0.035, 0.035],
  asymCheek: [0.96, 1.04],
} as const;

/** Minimum notch depth in normalised units — 4.8 px at the §7.7 48 px bar. */
export const MIN_NOTCH = 0.1;

/**
 * Draws one hull from a run seed. Pure, total, and stable: the same `runSeed` yields
 * byte-identical parameters on any machine, because it reads the same mulberry32 the sim
 * does, in a fixed order, and does nothing else.
 */
export function hullParamsForSeed(runSeed: number): HullParams {
  const rng = new RngStream(hullSeed(runSeed));
  const u = (r: readonly [number, number] | readonly number[]): number => r[0] + rng.next() * (r[1] - r[0]);
  const noseX = u(R.noseX);
  const cheekX = u(R.cheekX);
  const cheekY = u(R.cheekY);
  const camber = u(R.camber);
  const sweep = u(R.sweep);
  const span = u(R.span);
  const trailBack = u(R.trailBack);
  const trailFrac = u(R.trailFrac);
  const notchFrac = u(R.notchFrac);
  const trailX = sweep - trailBack;
  return {
    noseX,
    // Bimodal for the same reason `tipRake` is: a nose is either a point or a blunt
    // chord you can see, never a chord too small to resolve.
    noseHalf: rng.next() < 0.45 ? 0 : u(R.noseHalf),
    cheekX,
    cheekY,
    camber,
    sweep,
    span,
    trailBack,
    trailFrac,
    notch: Math.max(MIN_NOTCH, notchFrac * -trailX),
    // Bimodal on purpose: half the roster of hulls has a pointed wingtip and half a
    // squared one. A continuous range would spend most of its mass on rakes too small to
    // resolve at 48 px while still costing an outline vertex.
    tipRake: rng.next() < 0.5 ? 0 : u(R.tipRake),
    rearCamber: u(R.rearCamber),
    asymSpan: u(R.asymSpan),
    asymSweep: u(R.asymSweep),
    asymCheek: u(R.asymCheek),
  };
}

/**
 * The outline, nose-first and counter-clockwise, as a flat `[x, y, …]` ring.
 *
 * One closed outline, no separate masses. That is the structural reason a generated hull
 * cannot wander out of the family: there is no way to express a second body, a tentacle
 * crown or a tail fin in this representation. It is also why the whole thing is one fan
 * from the origin and therefore one mesh (§14.2).
 */
export function hullOutline(p: HullParams): number[] {
  const notchX = p.sweep - p.trailBack + p.notch;

  /**
   * One wing, shoulder → trailing edge, built entirely in upper-half coordinates. The
   * mirror is applied once, at the point of use, so the lower wing is the same
   * construction reflected and asymmetry is a parameter rather than a second code path.
   */
  const wing = (spanS: number, sweepS: number, cheekS: number): number[] => {
    const out: number[] = [];
    const cy = p.cheekY * cheekS;
    const tipX = sweepS;
    const tipY = spanS;
    out.push(p.cheekX, cy);
    if (p.camber !== 0) {
      // Midpoint of shoulder → wingtip, moved RADIALLY: the leading edge bows out (+) or
      // scythes back (-). Radially, not along the edge normal, and that is not a detail —
      // moving a vertex along its own ray cannot change its angle about the fan origin, so
      // the outline's winding stays monotone for every value in the range and the fan is
      // still exact. A normal-offset camber loses that at about ±0.04 and starts folding
      // triangles over each other, which is invisible in a wireframe and obvious in a rim.
      const mx = (p.cheekX + tipX) * 0.5;
      const my = (cy + tipY) * 0.5;
      out.push(mx * (1 + p.camber), my * (1 + p.camber));
    }
    out.push(tipX, tipY);
    // The wingtip's own chord. At 0 the tip is a point; at the top of the range it is a
    // squared-off blade 6 px across at the §7.7 bar, which is the difference between a
    // devil ray and a manta and is legible at the widest part of the silhouette.
    let rakeX = tipX;
    let rakeY = tipY;
    if (p.tipRake > 0) {
      rakeX = tipX - p.tipRake;
      rakeY = tipY - p.tipRake * 0.5;
      out.push(rakeX, rakeY);
    }
    const trX = rakeX - p.trailBack;
    const trY = rakeY * p.trailFrac;
    out.push(trX, trY);
    if (p.rearCamber !== 0) {
      // The trailing edge's own bow, on the midpoint of the trailing corner → notch chord
      // and moved radially for the same reason the leading edge's is. This is the lever
      // that decides whether the back of the animal is a straight cut, a filled-out sweep
      // or a swallowtail, and it is the strongest source of seed-to-seed difference that
      // costs nothing in enemy separation (correlation with the §11.12 margin: 0.03).
      out.push((trX + notchX) * 0.5 * (1 + p.rearCamber), trY * 0.5 * (1 + p.rearCamber));
    }
    return out;
  };

  const upper = wing(p.span, p.sweep, 1);
  const lower = wing(p.span * p.asymSpan, p.sweep + p.asymSweep, p.asymCheek);

  const pts: number[] = [];
  pts.push(p.noseX, p.noseHalf);
  for (let i = 0; i < upper.length; i++) pts.push(upper[i]);
  pts.push(notchX, 0);
  for (let i = lower.length - 2; i >= 0; i -= 2) pts.push(lower[i], -lower[i + 1]);
  if (p.noseHalf > 0) pts.push(p.noseX, -p.noseHalf);
  return pts;
}

/**
 * The fan's origin, on the body axis, 55% of the way from the nose-side of the body back
 * toward the notch apex.
 *
 * It is a parameter of the hull rather than a constant because it has to be, and finding
 * that out is the reason this file has a `winding` metric. A fan only tiles a polygon
 * exactly if the polygon is star-shaped about the origin, and for a delta wing whose
 * shoulder can sit *behind* an unswept wingtip, the coordinate origin is not such a point:
 * about 5% of the parameter space folds a triangle over its neighbour, which is invisible
 * in the fill and shows up as a doubled rim. Pinning the origin to a fraction of the notch
 * apex keeps it strictly inside the body and strictly forward of the notch for every
 * reachable parameter set, and `hullMetrics().winding` proves it over the population.
 */
export function hullOrigin(p: HullParams): number {
  return (p.sweep - p.trailBack + p.notch) * 0.55;
}

// --- AMD-50: the build, drawn ---------------------------------------------------------
//
// Two registers, and the split is forced by one measured number: the ship is 34 CSS px
// across on a 390 px phone (1.2 × a 0.7 u hitbox, at 20.1 px/u), and the neon stroke
// dilates the drawn boundary by 2 px on every side.
//
//  - **Silhouette.** The whole outline moves with the trait point-buy. This is the register
//    that survives 34 px, because it is the register that survives 8 px: it is area and
//    proportion, not detail. Nine of the fifteen hull parameters are shared between the run
//    seed and the build; six (`trailBack`, `trailFrac`, `notch` and the three `asym*`) stay
//    the seed's alone, so a ship still reads as YOURS after the build has reshaped it.
//  - **Marks.** Twelve stations on the outline: eight convex (four weapon hardpoints, four
//    passive families) and four concave (war damage). A convex mark survives the stroke's
//    dilation — dilation grows protrusions — and a concave one only survives if it is wider
//    than twice the stroke. That asymmetry is why the vocabulary is split the way it is:
//    **what you built pushes out, what the abyss took bites in.**
//
// Every mark is an insertion of vertices into the SAME closed ring, moved RADIALLY about
// the fan origin. That is the trick `camber` already uses, and it is what keeps all of this
// free: the outline stays star-shaped, `hullMetrics().winding` still proves the fan tiles
// it exactly, and the whole ship is still ONE geometry in ONE batch (§14.2).

/**
 * The build, as the geometry needs it. Structural on purpose — `ShipForm` in
 * `src/sim/build.ts` satisfies it, and so does a literal in a test or an offline tool, so
 * nothing here has to construct a `World` to draw a ship.
 */
export interface ShipFormView {
  /** Trait points [speed, defence, attack]; always sums to 12. */
  readonly points: ArrayLike<number>;
  /** Glyph index per weapon slot (0 spike, 1 fork, 2 blister, 3 plate), −1 for empty. */
  readonly slotGlyph: ArrayLike<number>;
  /** 1 when the slot's weapon has evolved. */
  readonly slotEvolved: ArrayLike<number>;
  /** Summed passive ranks per station [nose, crown, tail, keel]. */
  readonly familyRank: ArrayLike<number>;
  /** Concave bites earned by damage taken, 0..4. */
  readonly bites: number;
  /** Depth of those bites, 1..2. */
  readonly biteLevel: number;
  /** Steps earned by kills and bosses, 0..3. Folded into the tail crest. */
  readonly killSteps: number;
  readonly signature: number;
}

/** The 4/4/4 allocation an empty build gets. Mirrors `TRAIT_BASELINE`. */
const TRAIT_BASE = 4;
/** The largest a single trait can be away from the baseline (0..12 points on a 12 pool). */
const TRAIT_SPAN = 8;

/**
 * How each trait pulls each hull parameter, in [−1, 1], toward that parameter's own range
 * end. The user's own vocabulary, transcribed:
 *
 *   speed   — small, low-hulled, pointy   → thin body, swept wing, pointed nose and tips
 *   defence — round, bulky, thick         → full body, bowed edges, blunt nose and tips
 *   attack  — low-hulled, fast            → long scythed nose, thin body, swallowtail
 *
 * "Small" is expressed as silhouette AREA inside a fixed footprint, not as a smaller ship.
 * The hull is normalised so its furthest vertex sits at radius 1 whatever the build does,
 * which is the only reason a trait spend cannot move the §11 "brightest object" read, cannot
 * grow past the resting footprint AMD-44 measured its aerobatics against, and cannot
 * disagree with a collision circle that never changes.
 */
const TRAIT_PULL: Readonly<Record<string, readonly [number, number, number]>> = {
  noseX: [0.3, -0.6, 1.0],
  noseHalf: [-0.6, 1.0, -0.8],
  cheekX: [0.6, -0.6, 0.3],
  cheekY: [-0.7, 1.0, -0.6],
  camber: [-0.4, 1.0, -0.7],
  sweep: [-1.0, 0.9, -0.5],
  span: [0.6, -0.3, -0.2],
  tipRake: [-0.7, 1.0, -0.5],
  rearCamber: [-0.5, 1.0, -0.8],
};

/**
 * Fraction of the remaining room toward a range end that a fully-committed trait spends.
 *
 * A LERP toward the edge rather than an offset, and that is the whole safety argument: every
 * reachable hull is still inside the box AMD-43 measured 20,000 seeds in, so every §11.12
 * category-separation guarantee (min 0.25 signature distance to the nearest enemy, every
 * hull closer to the §12 manta than to any roster shape) survives a trait spend untouched.
 * No clamp is ever hit, because the interpolation cannot leave the interval it starts in.
 */
const TRAIT_GAIN = 0.55;

/** The two bimodal parameters treat 0 as their own low end; see `R`'s notes. */
const BIMODAL_LOW = 0;

function paramRange(key: string): readonly [number, number] {
  const r = (R as unknown as Record<string, readonly number[]>)[key];
  if (key === 'noseHalf' || key === 'tipRake') return [BIMODAL_LOW, r[1]];
  return [r[0], r[1]];
}

/** Net signed pull on one parameter, in [−1, 1]. */
function pullOf(key: string, points: ArrayLike<number>): number {
  const w = TRAIT_PULL[key];
  if (w === undefined) return 0;
  let n = 0;
  for (let t = 0; t < 3; t++) n += w[t] * (points[t] - TRAIT_BASE);
  n /= TRAIT_SPAN;
  return n > 1 ? 1 : n < -1 ? -1 : n;
}

function lerpToward(value: number, key: string, points: ArrayLike<number>): number {
  const n = pullOf(key, points) * TRAIT_GAIN;
  if (n === 0) return value;
  const range = paramRange(key);
  const edge = n > 0 ? range[1] : range[0];
  return value + Math.abs(n) * (edge - value);
}

/** The seed's hull, reshaped by the build's trait point-buy. Pure and total. */
export function traitHullParams(base: HullParams, points: ArrayLike<number>): HullParams {
  const sweep = lerpToward(base.sweep, 'sweep', points);
  const trailX = sweep - base.trailBack;
  return {
    noseX: lerpToward(base.noseX, 'noseX', points),
    noseHalf: lerpToward(base.noseHalf, 'noseHalf', points),
    cheekX: lerpToward(base.cheekX, 'cheekX', points),
    cheekY: lerpToward(base.cheekY, 'cheekY', points),
    camber: lerpToward(base.camber, 'camber', points),
    sweep,
    span: lerpToward(base.span, 'span', points),
    // The seed's alone. `notch` is re-derived because it is an offset off `sweep`, and a
    // reshaped wing that kept an absolute notch could cut its own trailing corner off.
    trailBack: base.trailBack,
    trailFrac: base.trailFrac,
    notch: Math.max(MIN_NOTCH, (base.notch / Math.max(1e-6, -(base.sweep - base.trailBack))) * -trailX),
    tipRake: lerpToward(base.tipRake, 'tipRake', points),
    rearCamber: lerpToward(base.rearCamber, 'rearCamber', points),
    asymSpan: base.asymSpan,
    asymSweep: base.asymSweep,
    asymCheek: base.asymCheek,
  };
}

/** The hull this run seed and this build fly together. */
export function hullParamsForForm(runSeed: number, form: ShipFormView): HullParams {
  return traitHullParams(hullParamsForSeed(runSeed), form.points);
}

// --- marks ----------------------------------------------------------------------------

/**
 * A station is a fixed bearing about the fan origin, nose at 0 and counter-clockwise.
 * Bearings, not vertex indices: the outline's vertex count changes with `camber`,
 * `tipRake` and `noseHalf`, and a mark that moved when the nose went blunt would not be
 * predictable. Every station keeps at least 8° of clear outline on both sides, which is
 * the spacing the 34 px legibility measurement in `tests/ship-marks.test.ts` is run at.
 */
const DEG = Math.PI / 180;
/** [nose, crown, hardpoint A, hardpoint B, tail, hardpoint C, hardpoint D, keel]. */
const STATION_THETA: readonly number[] = [0, 52, 100, 140, 180, 220, 260, 308].map((d) => d * DEG);
// nose 347–13 · wear 14–42 · crown 43–61 · hardpoints 89–111 / 129–151 · tail 167–193 ·
// hardpoints 209–231 / 249–271 · keel 299–317 · wear 318–346. No two overlap.
const STATION_HALF: readonly number[] = [13, 9, 11, 11, 13, 11, 11, 9].map((d) => d * DEG);
/**
 * Station index of each weapon slot, and of each passive family.
 *
 * Slots 0 and 1 take the two WINGTIPS, which is not decoration: a mark on an extremity
 * reproportions the whole silhouette and a mark on an interior edge does not. Measured at
 * 34 px, the same spike moves 56 px of silhouette at the wingtip and 3 px at the trailing
 * corner. Every run has a first and a second weapon; only a finished build has a fourth.
 */
const SLOT_STATION: readonly number[] = [2, 6, 3, 5];
const FAMILY_STATION: readonly number[] = [0, 1, 4, 7];
/**
 * War damage. TWO stations, not four, and wide — because a bite is CONCAVE and the neon
 * stroke dilates the drawn boundary by 2 px on every side. A notch narrower than twice the
 * stroke is filled in by the stroke's own copy and reaches the player as nothing at all;
 * measured, an 8° bite at 34 px moved 3 pixels. At 14° it is 7.5 px of arc, which clears
 * the dilation with 3.5 px to spare. Four narrow scars would have been four lies.
 *
 * Both sit on the bow, where a ship meets what it is flying into.
 */
const WEAR_THETA: readonly number[] = [28, 332].map((d) => d * DEG);
const WEAR_HALF = 14 * DEG;

/**
 * Radial profiles, sampled evenly across a mark's angular span. The first and last sample
 * is always 1 — a mark starts and ends ON the outline it grows out of, so the ring stays
 * closed and no vertex is orphaned.
 *
 * Four glyphs, and four is not a shortfall dressed up as a choice; it is the measured
 * budget. A mark 20° wide at the hardpoint radius is 4.4 CSS px of arc at phone size, and
 * 4.4 px after a 2 px dilation resolves a point, a split, a lobe and a slab — and nothing
 * finer. Twelve weapons therefore share four glyph classes at 34 px and separate fully only
 * on a surface that gives them more pixels.
 */
const PROFILE: Readonly<Record<string, readonly number[]>> = {
  // A point — but a STUBBY one. A needle profile ([0, 0.15, 1, 0.15, 0]) measured at 1 px
  // of change on a 486 px² silhouette: the 2 px stroke dilates it away. Mass, not sharpness.
  spike: [0, 0.62, 1, 0.62, 0],
  // Two prongs and a valley between them — the split every multi-projectile weapon has.
  // The valley is shallow for the same reason: a deep notch between two prongs is exactly
  // the concave feature the stroke erases, so it costs area and buys nothing.
  fork: [0, 1, 0.34, 1, 0],
  // One rounded lobe.
  blister: [0, 0.86, 1, 0.86, 0],
  // A squared slab: vertical sides, flat top. The most legible thing this vocabulary has,
  // because its whole outline is at full height rather than ramping to it.
  plate: [0.88, 1, 1, 1, 0.88],
  // War damage. Concave, so it must be deeper AND wider than the glyphs to survive the
  // same dilation — see `BITE_D` and `WEAR_HALF`.
  bite: [0, -0.8, -1, -0.8, 0],
};
const GLYPH_ORDER: readonly string[] = ['spike', 'fork', 'blister', 'plate'];

/** Height of a weapon's glyph as a fraction of the local radius: base, then evolved. */
const GLYPH_H: readonly number[] = [0.19, 0.3];
/**
 * Height of a passive family's mark by level. Level is a step function of summed rank
 * (1–4 → 1, 5–9 → 2, 10+ → 3) so a station moves a few times per run rather than on every
 * pick: the hull geometry is rebuilt when it moves, and a rebuild is a buffer upload.
 */
const FAMILY_H: readonly number[] = [0, 0.12, 0.21, 0.3];
/** Depth of a bite by level. */
const BITE_D: readonly number[] = [0, 0.18, 0.3];
/** What one kill step adds to the tail crest. */
const KILL_STEP_H = 0.05;

export function familyLevel(rank: number): number {
  if (rank <= 0) return 0;
  if (rank < 5) return 1;
  if (rank < 10) return 2;
  return 3;
}

interface Mark {
  theta: number;
  half: number;
  profile: readonly number[];
  height: number;
}

/**
 * Radius of the outline along a bearing from the fan origin. Exact: the ring winds
 * monotonically about that origin (AMD-43's `winding` metric is the proof), so exactly one
 * segment crosses any ray and a linear solve on it is the answer rather than an estimate.
 */
function radiusAt(ring: readonly number[], ox: number, theta: number): number {
  const dx = Math.cos(theta);
  const dy = Math.sin(theta);
  const n = ring.length / 2;
  let best = 0;
  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    const ax = ring[i * 2] - ox;
    const ay = ring[i * 2 + 1];
    const bx = ring[j * 2] - ox;
    const by = ring[j * 2 + 1];
    // Ray-segment intersection: solve a + u(b − a) = t·d for u in [0,1], t ≥ 0.
    const den = (bx - ax) * dy - (by - ay) * dx;
    if (Math.abs(den) < 1e-12) continue;
    const u = (ay * dx - ax * dy) / den;
    if (u < 0 || u > 1) continue;
    const px = ax + u * (bx - ax);
    const py = ay + u * (by - ay);
    const t = px * dx + py * dy;
    if (t > best) best = t;
  }
  return best;
}

/** Bearing of a ring vertex about the fan origin, in [0, 2π). */
function bearing(ring: readonly number[], ox: number, i: number): number {
  const a = Math.atan2(ring[i * 2 + 1], ring[i * 2] - ox);
  return a < 0 ? a + Math.PI * 2 : a;
}

/**
 * Splices a set of marks into a closed ring.
 *
 * Every inserted vertex is placed on its own RAY out of the fan origin and then scaled
 * along it, so the ring's bearings stay strictly increasing and the fan stays exact — the
 * same argument `camber` rests on, applied to twelve places instead of one. Marks are
 * required not to overlap in bearing; the station table guarantees ≥ 8° of clear outline
 * between any two, and `tests/ship-marks.test.ts` asserts it.
 */
function spliceMarks(ring: readonly number[], ox: number, marks: readonly Mark[]): number[] {
  if (marks.length === 0) return ring.slice();
  const sorted = marks.slice().sort((a, b) => a.theta - b.theta);
  const out: number[] = [];
  const covered = (theta: number): boolean => {
    for (let m = 0; m < sorted.length; m++) {
      let d = theta - sorted[m].theta;
      while (d > Math.PI) d -= Math.PI * 2;
      while (d < -Math.PI) d += Math.PI * 2;
      if (Math.abs(d) < sorted[m].half) return true;
    }
    return false;
  };

  const n = ring.length / 2;
  const events: { theta: number; kind: number; mark: number }[] = [];
  for (let i = 0; i < n; i++) {
    const t = bearing(ring, ox, i);
    if (!covered(t)) events.push({ theta: t, kind: 0, mark: i });
  }
  for (let m = 0; m < sorted.length; m++) {
    let t = sorted[m].theta;
    if (t < 0) t += Math.PI * 2;
    events.push({ theta: t, kind: 1, mark: m });
  }
  events.sort((a, b) => a.theta - b.theta || a.kind - b.kind);

  for (let e = 0; e < events.length; e++) {
    const ev = events[e];
    if (ev.kind === 0) {
      out.push(ring[ev.mark * 2], ring[ev.mark * 2 + 1]);
      continue;
    }
    const mk = sorted[ev.mark];
    const prof = mk.profile;
    const steps = prof.length - 1;
    for (let k = 0; k <= steps; k++) {
      const s = -1 + (2 * k) / steps;
      // The end samples sit a hair inside the span so a mark never shares a bearing with
      // its neighbour's end sample, which would make two vertices collinear with the
      // origin and collapse a fan triangle to zero area.
      const theta = mk.theta + s * mk.half * 0.995;
      const r = radiusAt(ring, ox, theta) * (1 + prof[k] * mk.height);
      out.push(ox + Math.cos(theta) * r, Math.sin(theta) * r);
    }
  }
  return out;
}

/** Every mark this build wears, in station order. Pure; allocates only at build time. */
export function shipMarks(form: ShipFormView): Mark[] {
  const marks: Mark[] = [];
  for (let s = 0; s < 4; s++) {
    const g = form.slotGlyph[s];
    if (g < 0) continue;
    const evolved = form.slotEvolved[s] > 0 ? 1 : 0;
    const st = SLOT_STATION[s];
    marks.push({
      theta: STATION_THETA[st],
      half: STATION_HALF[st] * (evolved ? 1 : 0.86),
      profile: PROFILE[GLYPH_ORDER[g] ?? 'spike'],
      height: GLYPH_H[evolved],
    });
  }
  for (let f = 0; f < 4; f++) {
    const level = familyLevel(form.familyRank[f]);
    // The tail carries the run's little wins as well as its endurance picks: at 34 px there
    // is no eighth free bearing for a kill counter, and both readings are "veteran".
    const extra = f === 2 ? form.killSteps * KILL_STEP_H : 0;
    if (level === 0 && extra === 0) continue;
    const st = FAMILY_STATION[f];
    marks.push({
      theta: STATION_THETA[st],
      half: STATION_HALF[st],
      profile: PROFILE[GLYPH_ORDER[f] ?? 'spike'],
      height: FAMILY_H[level] + extra,
    });
  }
  // Four earned bites over two stations: the first opens the upper bow, the second the
  // lower, the third and fourth deepen them in the same order. Monotone, so a scar never
  // heals, and there is no fifth place on a 34 px outline for a fifth one to go.
  for (let b = 0; b < WEAR_THETA.length; b++) {
    let level = Math.floor((form.bites - b + 1) / 2);
    if (level > 2) level = 2;
    if (level <= 0) continue;
    marks.push({ theta: WEAR_THETA[b], half: WEAR_HALF, profile: PROFILE.bite, height: BITE_D[level] });
  }
  return marks;
}

/** The outline with every mark spliced in. One closed ring, still star-shaped. */
export function markedOutline(p: HullParams, form: ShipFormView): number[] {
  return spliceMarks(hullOutline(p), hullOrigin(p), shipMarks(form));
}

/**
 * The hull as triangle soup: one fan, one mesh, ~12 triangles. §14.2 budgets the player
 * at two meshes and a procedural silhouette must not become N.
 */
export function buildHull(p: HullParams): ShapeBuilder {
  return new ShapeBuilder().fan(hullOutline(p), hullOrigin(p), 0);
}

/** The per-run player silhouette. Built once in `Renderer.install`, never per frame. */
export function buildPlayerGeometry(runSeed = 0, form: ShipFormView | null = null): THREE.BufferGeometry {
  const p = form === null ? hullParamsForSeed(runSeed) : hullParamsForForm(runSeed, form);
  const ring = form === null ? hullOutline(p) : markedOutline(p, form);
  return new ShapeBuilder()
    .fan(ring, hullOrigin(p), 0)
    .normalize()
    .build(`player-${runSeed >>> 0}-${form === null ? 0 : form.signature >>> 0}`);
}

/** Spine §12's ratified manta, unchanged. The family anchor and the fallback. */
export function buildReferenceGeometry(): THREE.BufferGeometry {
  return buildHull(REFERENCE_HULL).normalize().build('player-reference');
}

// --- measurement ---------------------------------------------------------------------
//
// Everything below is analysis, not rendering: it is what the tests and the contact-sheet
// tool use to assert that a hull is legible, bounded, still a manta and not an enemy. It
// is pure, allocation-heavy and never called from a frame.

/** A normalised outline, scaled so its furthest vertex sits at radius 1 (as `normalize`). */
export function normalizedOutline(p: HullParams): number[] {
  const pts = hullOutline(p);
  let max = 0;
  for (let i = 0; i < pts.length; i += 2) {
    const r = Math.hypot(pts[i], pts[i + 1]);
    if (r > max) max = r;
  }
  const k = max > 0 ? 1 / max : 1;
  for (let i = 0; i < pts.length; i++) pts[i] *= k;
  return pts;
}

export interface HullMetrics {
  /** Area of the silhouette, in units of the unit disc's bounding square. */
  area: number;
  /** Area centroid on the heading axis. */
  cx: number;
  /**
   * Third standardised moment of the mass distribution along the heading axis. Positive
   * means the long thin tail of the distribution points FORWARD — the arrowhead read, and
   * the single number that says which end is the front.
   */
  skew: number;
  /** How far the nose reaches ahead of the centroid, over how far the tail reaches behind. */
  reach: number;
  /** Wingspan over body length. */
  aspect: number;
  /** Silhouette area over convex-hull area — compactness at low resolution. */
  solidity: number;
  /** Shortest outline edge, in normalised units. */
  minEdge: number;
  /** Half-angle of the wedge at the nose, in radians. */
  noseWedge: number;
  /** Notch depth as built, after normalisation. */
  notch: number;
  /** Furthest vertex from the body origin. Bounds the drawn extent. */
  extent: number;
  /** True when the outline winds monotonically about the fan origin. */
  winding: boolean;
}

/** Exact area and the first three x-moments, by scanning the polygon row by row. */
function moments(ring: number[], rows = 512): { a: number; m1: number; m2: number; m3: number } {
  let minY = Infinity;
  let maxY = -Infinity;
  for (let i = 1; i < ring.length; i += 2) {
    if (ring[i] < minY) minY = ring[i];
    if (ring[i] > maxY) maxY = ring[i];
  }
  const dy = (maxY - minY) / rows;
  let a = 0;
  let m1 = 0;
  let m2 = 0;
  let m3 = 0;
  const xs: number[] = [];
  for (let r = 0; r < rows; r++) {
    const y = minY + (r + 0.5) * dy;
    xs.length = 0;
    for (let i = 0; i < ring.length; i += 2) {
      const j = (i + 2) % ring.length;
      const y0 = ring[i + 1];
      const y1 = ring[j + 1];
      if (y0 === y1) continue;
      if ((y < y0) === (y < y1)) continue;
      xs.push(ring[i] + ((y - y0) / (y1 - y0)) * (ring[j] - ring[i]));
    }
    xs.sort((p, q) => p - q);
    for (let k = 0; k + 1 < xs.length; k += 2) {
      const x0 = xs[k];
      const x1 = xs[k + 1];
      a += (x1 - x0) * dy;
      m1 += ((x1 * x1 - x0 * x0) / 2) * dy;
      m2 += ((x1 ** 3 - x0 ** 3) / 3) * dy;
      m3 += ((x1 ** 4 - x0 ** 4) / 4) * dy;
    }
  }
  return { a, m1, m2, m3 };
}

function convexHullArea(ring: number[]): number {
  const pts: number[][] = [];
  for (let i = 0; i < ring.length; i += 2) pts.push([ring[i], ring[i + 1]]);
  pts.sort((p, q) => (p[0] === q[0] ? p[1] - q[1] : p[0] - q[0]));
  const cross = (o: number[], a: number[], b: number[]): number =>
    (a[0] - o[0]) * (b[1] - o[1]) - (a[1] - o[1]) * (b[0] - o[0]);
  const build = (src: number[][]): number[][] => {
    const out: number[][] = [];
    for (const pt of src) {
      while (out.length >= 2 && cross(out[out.length - 2], out[out.length - 1], pt) <= 0) out.pop();
      out.push(pt);
    }
    out.pop();
    return out;
  };
  const hull = build(pts).concat(build(pts.slice().reverse()));
  let a = 0;
  for (let i = 0; i < hull.length; i++) {
    const j = (i + 1) % hull.length;
    a += hull[i][0] * hull[j][1] - hull[j][0] * hull[i][1];
  }
  return Math.abs(a) / 2;
}

export function hullMetrics(p: HullParams): HullMetrics {
  const ring = normalizedOutline(p);
  const n = ring.length / 2;
  // The winding test below is asked about the fan's own origin, which is the only point
  // the exactness of the triangulation depends on. `normalizedOutline` has scaled the
  // outline, so the origin scales with it.
  const ox = hullOrigin(p) * (ring[0] / hullOutline(p)[0]);
  const { a, m1, m2, m3 } = moments(ring);
  const cx = m1 / a;
  const varX = m2 / a - cx * cx;
  const sd = Math.sqrt(Math.max(varX, 1e-12));
  const skew = (m3 / a - 3 * cx * (m2 / a) + 2 * cx ** 3) / sd ** 3;

  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;
  let extent = 0;
  let minEdge = Infinity;
  let winding = true;
  for (let i = 0; i < n; i++) {
    const x = ring[i * 2];
    const y = ring[i * 2 + 1];
    if (x < minX) minX = x;
    if (x > maxX) maxX = x;
    if (y < minY) minY = y;
    if (y > maxY) maxY = y;
    const r = Math.hypot(x, y);
    if (r > extent) extent = r;
    const jx = ring[((i + 1) % n) * 2];
    const jy = ring[((i + 1) % n) * 2 + 1];
    const e = Math.hypot(jx - x, jy - y);
    if (e < minEdge) minEdge = e;
    // Monotonic winding about the fan origin: every fan triangle has positive area, so
    // the fan tiles the outline exactly.
    if ((x - ox) * jy - y * (jx - ox) <= 0) winding = false;
  }

  // Nose wedge: the angle between the axis and the first leading edge off the nose.
  const noseWedge = Math.atan2(Math.abs(ring[3] - ring[1]), Math.abs(ring[0] - ring[2]));
  // Notch depth, after normalisation: the trailing corner's x against the axis vertex's.
  const notchIdx = ring.findIndex((_, i) => i % 2 === 1 && Math.abs(ring[i]) < 1e-9 && i > 1);
  const notchX = notchIdx > 0 ? ring[notchIdx - 1] : minX;

  return {
    area: a,
    cx,
    skew,
    reach: (maxX - cx) / (cx - minX),
    aspect: (maxY - minY) / (maxX - minX),
    solidity: a / convexHullArea(ring),
    minEdge,
    noseWedge,
    notch: notchX - minX,
    extent,
    winding,
  };
}

// --- silhouette similarity -------------------------------------------------------------

/**
 * §7.7's readability bar is identification at 48 px, so that is the resolution the
 * §11.12 category-separation question is asked at: two shapes are confusable if they
 * cover the same pixels once they are the size a player actually reads them at.
 */
export const RASTER = 48;

export type Mask = Uint8Array;

/** Rasterises a closed outline over [-1,1]² at {@link RASTER}. */
export function rasterizeOutline(ring: number[], n = RASTER): Mask {
  const m = new Uint8Array(n * n);
  const xs: number[] = [];
  for (let r = 0; r < n; r++) {
    const y = -1 + ((r + 0.5) / n) * 2;
    xs.length = 0;
    for (let i = 0; i < ring.length; i += 2) {
      const j = (i + 2) % ring.length;
      const y0 = ring[i + 1];
      const y1 = ring[j + 1];
      if (y0 === y1) continue;
      if ((y < y0) === (y < y1)) continue;
      xs.push(ring[i] + ((y - y0) / (y1 - y0)) * (ring[j] - ring[i]));
    }
    xs.sort((p, q) => p - q);
    for (let k = 0; k + 1 < xs.length; k += 2) {
      const c0 = Math.max(0, Math.ceil(((xs[k] + 1) / 2) * n - 0.5));
      const c1 = Math.min(n - 1, Math.floor(((xs[k + 1] + 1) / 2) * n - 0.5));
      for (let c = c0; c <= c1; c++) m[r * n + c] = 1;
    }
  }
  return m;
}

/** Rasterises triangle soup — the form every enemy and boss silhouette is built in. */
export function rasterizeTriangles(pos: ArrayLike<number>, n = RASTER): Mask {
  const m = new Uint8Array(n * n);
  const step = 2 / n;
  for (let t = 0; t + 8 < pos.length; t += 9) {
    const ax = pos[t];
    const ay = pos[t + 1];
    const bx = pos[t + 3];
    const by = pos[t + 4];
    const cx = pos[t + 6];
    const cy = pos[t + 7];
    const d = (by - cy) * (ax - cx) + (cx - bx) * (ay - cy);
    if (Math.abs(d) < 1e-12) continue;
    const r0 = Math.max(0, Math.floor(((Math.min(ay, by, cy) + 1) / step) | 0));
    const r1 = Math.min(n - 1, Math.ceil((Math.max(ay, by, cy) + 1) / step));
    const c0 = Math.max(0, Math.floor(((Math.min(ax, bx, cx) + 1) / step) | 0));
    const c1 = Math.min(n - 1, Math.ceil((Math.max(ax, bx, cx) + 1) / step));
    for (let r = r0; r <= r1; r++) {
      const y = -1 + (r + 0.5) * step;
      for (let c = c0; c <= c1; c++) {
        if (m[r * n + c]) continue;
        const x = -1 + (c + 0.5) * step;
        const l1 = ((by - cy) * (x - cx) + (cx - bx) * (y - cy)) / d;
        if (l1 < 0 || l1 > 1) continue;
        const l2 = ((cy - ay) * (x - cx) + (ax - cx) * (y - cy)) / d;
        if (l2 < 0 || l2 > 1) continue;
        const l3 = 1 - l1 - l2;
        if (l3 < 0 || l3 > 1) continue;
        m[r * n + c] = 1;
      }
    }
  }
  return m;
}

/** Intersection over union of two masks. 1 is the same picture, 0 is no shared pixel. */
export function iou(a: Mask, b: Mask): number {
  let inter = 0;
  let union = 0;
  for (let i = 0; i < a.length; i++) {
    const x = a[i];
    const y = b[i];
    if (x | y) union++;
    if (x & y) inter++;
  }
  return union === 0 ? 0 : inter / union;
}

/**
 * The mask's radial signature: the outline's distance from its own area centroid, in 64
 * directions, divided by its mean.
 *
 * The companion to `iou`, and the one that answers the question §11.12 actually asks.
 * These silhouettes render as a white rim around a flat core, so what a player matches on
 * is the BOUNDARY, and two shapes can overlap heavily in area while their outlines say
 * completely different animals — a manta and the ventspitter's cone share 0.59 of their
 * pixels at 48 px and no player has ever confused them. Scale-invariant by construction,
 * and rotation is not normalised out because every shape in this game is drawn nose-first.
 */
export function radialSignature(m: Mask, n = RASTER, bins = 64): Float32Array {
  let sx = 0;
  let sy = 0;
  let count = 0;
  for (let r = 0; r < n; r++) {
    for (let c = 0; c < n; c++) {
      if (!m[r * n + c]) continue;
      sx += -1 + ((c + 0.5) / n) * 2;
      sy += -1 + ((r + 0.5) / n) * 2;
      count++;
    }
  }
  const out = new Float32Array(bins);
  if (count === 0) return out;
  sx /= count;
  sy /= count;
  for (let r = 0; r < n; r++) {
    for (let c = 0; c < n; c++) {
      if (!m[r * n + c]) continue;
      const dx = -1 + ((c + 0.5) / n) * 2 - sx;
      const dy = -1 + ((r + 0.5) / n) * 2 - sy;
      let a = Math.atan2(dy, dx) / (Math.PI * 2);
      if (a < 0) a += 1;
      const bin = Math.min(bins - 1, (a * bins) | 0);
      const d = Math.hypot(dx, dy);
      if (d > out[bin]) out[bin] = d;
    }
  }
  let mean = 0;
  for (let i = 0; i < bins; i++) mean += out[i];
  mean /= bins;
  if (mean > 0) for (let i = 0; i < bins; i++) out[i] /= mean;
  return out;
}

/** RMS distance between two radial signatures. 0 is the same outline. */
export function signatureDistance(a: Float32Array, b: Float32Array): number {
  let s = 0;
  for (let i = 0; i < a.length; i++) s += (a[i] - b[i]) ** 2;
  return Math.sqrt(s / a.length);
}

/** How much of the §12 manta a hull still is. The family test, not a similarity score. */
export function hullSimilarity(p: HullParams, reference = REFERENCE_HULL): number {
  return iou(rasterizeOutline(normalizedOutline(p)), rasterizeOutline(normalizedOutline(reference)));
}
